import React, { useRef, useState, useImperativeHandle, useEffect } from 'react';
import { Phone, Video } from 'lucide-react';
import { Database } from '../types/database';
import { getDisplayName, isVideoUrl } from '../lib/utils';
import { isFromAppStores } from '@/lib/detection';

export type Character = Database['eh']['Tables']['characters']['Row'];

interface CharacterCardProps {
  character: Character;
  activeCardId: string | null;
  setActiveCardId: (id: string | null) => void;
}

export type CharacterCardRef = {
  playImperative: () => void;
  stopImperative: () => void;
  ref: () => HTMLDivElement | null;
}

const CharacterCard = React.forwardRef<CharacterCardRef, CharacterCardProps>(
  function CharacterCard({ character, activeCardId, setActiveCardId }, ref) {
  const isActive = activeCardId === character.id;
  const mainMedia = character.profile_media_fpaths?.[0];
  const altMedia = character.profile_media_fpaths?.[1];
  const [altLoaded, setAltLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [forcePlayOnLoad, setForcePlayOnLoad] = useState(false);
  const [imperativePlay, setImperativePlay] = useState(false);
  const [mainLoaded, setMainLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => {
    return {
      playImperative: () => setImperativePlay(true),
      stopImperative: () => {
        setImperativePlay(false);
        if (import.meta.env.DEV && imperativePlay) console.log('[DEV] Stopping video', character.url_slug);
      },
      ref: () => cardRef.current,
    };
  });

  // Handlers for hover/touch
  const handleMouseEnter = () => setActiveCardId(character.id);
  const handleMouseLeave = () => {
    setActiveCardId(null);
    // autoplay video on load after leaving the card so users don't miss it on slow networks
    setForcePlayOnLoad(!videoLoaded);
  };
  const handleTouchStart = () => setActiveCardId(character.id);
  const handleTouchEnd = () => setActiveCardId(character.id); // after scroll

  // Reset altLoaded when card is deactivated
  useEffect(() => {
    if (!isActive) setAltLoaded(false);
  }, [isActive]);

  // Loader timer effect
  useEffect(() => {
    if (!mainMedia || mainLoaded) {
      setShowLoader(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowLoader(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [mainMedia, mainLoaded]);

  const isActiveOrPlaying = isActive || forcePlayOnLoad || imperativePlay;

  return (
    <div
      ref={cardRef}
      data-char-id={character.id}
      className="relative bg-zinc-800 rounded-2xl shadow-lg overflow-hidden flex flex-col h-full group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      // onClick={onClick}
      // tabIndex={0} // enable if click handler is needed
      aria-label={`Character card for ${character.first_name}`}
    >
      {/* Media stack */}
      <div className="relative w-full h-full min-h-72 aspect-[3/4]">
        {/* LQIP blurred placeholder (under main media) */}
        {mainMedia && character.lqip && (
          <img
            src={`data:image/webp;base64,${character.lqip}`}
            alt="Low quality placeholder"
            className="w-full h-full object-cover object-center absolute inset-0 z-0 blur-md scale-105 transition-opacity duration-300"
            draggable={false}
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* Main media always visible underneath */}
        {mainMedia ? (
          <img
            key={mainMedia}
            src={mainMedia}
            alt={character.first_name ?? 'Evening Honey AI Character'}
            className={`w-full h-full object-cover object-center absolute inset-0 z-10 transition-all duration-300 ${
              isActive && !altMedia ? 'scale-105' : ''
            }`}
            loading="lazy"
            draggable={false}
            style={{ pointerEvents: 'none', opacity: mainLoaded ? 1 : 0 }}
            onLoad={() => setMainLoaded(true)}
          />
        ) : (
          
          <div className="w-full h-full flex items-center justify-center text-4xl text-pink-700 bg-zinc-700 absolute inset-0 z-0">
            {character.first_name[0] + character.last_name?.[0]}
          </div>
        )}
        {/* Loader overlay if image is slow to load */}
        {mainMedia && !mainLoaded && showLoader && !(isActiveOrPlaying && altLoaded) && (
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="animate-spin size-8 border-4 border-white/30 border-t-white rounded-full" />
          </div>
        )}
        {/* Alt media overlays and fades in when loaded and active */}
        {altMedia && isActiveOrPlaying && (
          isVideoUrl(altMedia) ? (
            <video
              key={altMedia}
              src={altMedia}
              className="w-full h-full object-cover object-center absolute inset-0 z-10 transition-opacity duration-500"
              autoPlay
              loop
              muted
              playsInline
              aria-label="Character video preview"
              style={{ opacity: altLoaded ? 1 : 0, pointerEvents: 'none' }}
              onCanPlay={() => setAltLoaded(true)}
              onLoadedData={() => setVideoLoaded(true)}
            />
          ) : (
            <img
              key={altMedia}
              src={altMedia}
              alt={character.first_name ?? 'Evening Honey AI Character'}
              className="w-full h-full object-cover object-center absolute inset-0 z-10 transition-opacity duration-500"
              loading="lazy"
              draggable={false}
              style={{ opacity: altLoaded ? 1 : 0, pointerEvents: 'none' }}
              onLoad={() => setAltLoaded(true)}
            />
          )
        )}
        {/* Gradient overlay for text readability */}
        <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-zinc-900/90 to-transparent pointer-events-none z-20" />
        {/* Message and Call icons */}
        <div className="absolute top-3 right-3 z-30 flex flex-col items-end gap-2">
          {/* <button
            className="bg-pink-700 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg"
            aria-label="Start chat"
          >
            <MessageCircle size={22} />
          </button> */}
          <button
            className="flex items-center gap-1 rounded-full bg-pink-700 px-2 py-1.5 text-white shadow-lg hover:bg-pink-600 opacity-80"
            aria-label="Start phone or video call"
          >
            <Phone size={18} />
            <span className="-translate-y-px text-xl leading-none text-white/60">/</span>
            <Video size={18} />
          </button>
        </div>
      </div>
      {/* Info overlayed at bottom */}
      <div className={`absolute bottom-0 left-0 w-full px-4 pb-2 pt-10 flex flex-col z-30 transition-opacity duration-300 ${
        isActive ? 'opacity-40' : ''}`
      }>
        <div className="flex text-lg md:text-2xl font-bold drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
          <span className="flex-[1_1_auto] overflow-hidden text-ellipsis text-nowrap">{getDisplayName(character)}</span>
          <span className="text-zinc-300 pl-2 font-semibold drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">{` ${character.age}`}</span>
        </div>
        <div className="text-sm text-zinc-200 mb-2 line-clamp-1 drop-shadow">
          <span>{character.aboutme || '\u00A0'}</span>
        </div>
        <div
          className="flex max-w-full overflow-y-auto gap-1 pr-3"
          style={{
            scrollbarWidth: 'none',
            maskImage: 'linear-gradient(to right, black 94%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 94%, transparent 100%)',
          }}
        >
          <span key={character.personality} className="px-2 py-0.5 rounded-full bg-zinc-700 text-xs text-pink-300 capitalize text-nowrap">
            {character.personality}
          </span>
          {!isFromAppStores() && character.kinks?.map((k) => (
            <span key={k} className="px-2 py-0.5 rounded-full bg-pink-900 text-xs text-pink-200 capitalize text-nowrap">
              {k}
            </span>
          ))} 
        </div>
      </div>
    </div>
  );
});

export default CharacterCard; 
